import axios from 'axios';
import { clearAllDraftsForUser } from './../utils/tacticsDrafts';
import { requestAdminStepUp } from './adminStepUp';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance pointing directly at backend — no proxy dependency
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

function resolveViewerTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function attachAdminConfirmHeader(config) {
  const url = String(config.url || '');
  const path = url.startsWith('http') ? new URL(url).pathname : url;
  const patterns = [
    /^\/admin\/users\/([^/]+)$/,
    /^\/admin\/users\/([^/]+)\/plan$/,
    /^\/admin\/users\/([^/]+)\/role$/,
    /^\/admin\/users\/([^/]+)\/revoke-plan$/,
    /^\/admin\/users\/([^/]+)\/set-password$/,
    /^\/admin\/users\/([^/]+)\/reset-mfa$/,
    /^\/admin\/clubs\/([^/]+)\/revoke-plan$/,
    /^\/admin\/clubs\/([^/]+)\/revoke-subscription$/,
  ];
  for (const p of patterns) {
    const m = path.match(p);
    if (m && m[1]) {
      config.headers = config.headers || {};
      config.headers['x-admin-confirm'] = m[1];
      break;
    }
  }
  return config;
}

function emitAnalyticsEvent(event, details = {}) {
  if (typeof window === 'undefined' || !event) return;
  window.dispatchEvent(new CustomEvent('eliteedge:analytics', {
    detail: { event, ...details },
  }));
}

function requestPath(config) {
  const url = String(config?.url || '');
  try {
    return new URL(url, 'https://eliteedge.local').pathname;
  } catch {
    return url.split('?')[0];
  }
}

function requestBody(config) {
  const data = config?.data;
  if (!data) return {};
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
}

function privilegedActionLabel(config) {
  const path = requestPath(config);
  if (/\/role$/.test(path)) return "change this user's role";
  if (/\/set-password$/.test(path)) return "set this user's temporary password";
  if (/\/recovery-link/.test(path) || /\/recovery-links$/.test(path)) return 'generate a recovery link';
  if (/\/revoke-subscription$/.test(path)) return 'revoke this club subscription';
  if (/\/revoke-plan$/.test(path)) return 'revoke this plan';
  if (/\/plan$/.test(path)) return 'change this subscription plan';
  return 'complete this sensitive admin action';
}

function aiFeatureForPath(path) {
  if (path === '/ai/chat') return 'coach_chat';
  if (path === '/ai/training-plan') return 'training_plan';
  if (path === '/ai/team-analysis') return 'team_analysis';
  if (path === '/ai/analyze-player') return 'player_analysis';
  if (path === '/ai/match-analysis' || /^\/ai\/matches\/[^/]+\/(debrief|expand-recap)$/.test(path) || path === '/ai/matches/expand-recap-draft') return 'match_analysis';
  if (path === '/ai/drill-layout' || path === '/ai/regenerate-drill') return 'drill_generation';
  if (path === '/ai/debrief') return 'session_debrief';
  if (path === '/ai/voice-notes') return 'voice_notes';
  if (/^\/ai\/scheduled-reports\/[^/]+\/generate$/.test(path) || path === '/ai/scheduled-reports/send-once') return 'scheduled_report';
  if (/^\/player-folder\/[^/]+\/programs\/generate$/.test(path)) return 'development_program';
  if (path === '/training-blocks/generate') return 'training_block';
  return '';
}

function trackSuccessfulMilestone(response) {
  try {
    const config = response?.config || {};
    const method = String(config.method || 'get').toLowerCase();
    const path = requestPath(config);
    const body = requestBody(config);

    if (
      method === 'post'
      && (path === '/auth/login' || path === '/auth/mfa/verify-login')
      && response?.data?.user
      && !response?.data?.mfaRequired
    ) {
      emitAnalyticsEvent('login_success', {
        method: path.includes('mfa') ? 'mfa' : 'password',
      });
      return;
    }

    if (method === 'post' && path === '/auth/verify-email') {
      emitAnalyticsEvent('email_verified');
      return;
    }

    if (method === 'patch' && path === '/auth/onboarding') {
      const actionEvents = {
        viewJourney: 'onboarding_started',
        completeTour: 'onboarding_tour_completed',
        completeJourney: 'onboarding_completed',
      };
      if (actionEvents[body.action]) {
        emitAnalyticsEvent(actionEvents[body.action]);
      } else if (
        body.action === 'completeStep'
        && typeof body.step === 'string'
        && /^[a-z0-9_-]{1,40}$/.test(body.step)
      ) {
        emitAnalyticsEvent('onboarding_step_completed', { step: body.step });
      }
      return;
    }

    if (method === 'post' && path === '/auth/onboarding/club') {
      emitAnalyticsEvent('club_created', { source: 'self_serve_onboarding' });
      if (typeof body.teamName === 'string' && body.teamName.trim()) {
        emitAnalyticsEvent('team_created', { source: 'self_serve_onboarding' });
      }
      return;
    }

    if (method === 'post' && path === '/teams') {
      emitAnalyticsEvent('team_created', { source: 'team_manager' });
      return;
    }
    if (method === 'post' && path === '/players') {
      emitAnalyticsEvent('player_created');
      return;
    }
    if (method === 'post' && path === '/sessions') {
      emitAnalyticsEvent('session_created');
      return;
    }

    if (method === 'post') {
      const feature = aiFeatureForPath(path);
      if (feature) emitAnalyticsEvent('ai_action_completed', { feature });
    }
  } catch {
    // Analytics failures must never change a successful product action.
  }
}

api.interceptors.request.use((config) => {
  return attachAdminConfirmHeader(config);
});

// Handle auth errors globally — clear session and redirect to login
api.interceptors.response.use(
  (response) => {
    trackSuccessfulMilestone(response);
    return response;
  },
  async (error) => {
    const stepUpCode = error.response?.data?.code;
    if (
      error.response?.status === 401
      && stepUpCode === 'ADMIN_STEP_UP_REQUIRED'
      && !error.config?._adminStepUpRetried
    ) {
      try {
        const config = error.config;
        return await requestAdminStepUp({
          mode: error.response.data.mode,
          message: error.response.data.error,
          action: privilegedActionLabel(config),
          verify: async ({ mode, credential }) => {
            const headerName = mode === 'mfa' ? 'x-admin-mfa-code' : 'x-admin-password';
            config._adminStepUpRetried = true;
            config.headers = config.headers || {};
            config.headers[headerName] = credential;
            try {
              return await api.request(config);
            } finally {
              delete config.headers[headerName];
            }
          },
        });
      } catch (stepUpError) {
        if (stepUpError?.code === 'ADMIN_STEP_UP_CANCELLED' || stepUpError?.code === 'ADMIN_STEP_UP_BUSY') {
          stepUpError.response = { data: { error: 'Admin action cancelled' } };
          return Promise.reject(stepUpError);
        }
        if (stepUpError?.response) return Promise.reject(stepUpError);
        return Promise.reject(error);
      }
    }

    if (stepUpCode && stepUpCode.startsWith('ADMIN_STEP_UP_')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        // Backend serializes the user as { id, ... } not { _id, ... } — tolerate both.
        const uid = u && (u._id || u.id);
        if (uid) clearAllDraftsForUser(uid);
      } catch {}
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  legalCurrent: () => api.get('/auth/legal/current'),
  legalAccept: (data) => api.post('/auth/legal/accept', data),
  oauthSetupProfile: () => api.get('/auth/oauth/setup'),
  oauthSetupComplete: (data) => api.post('/auth/oauth/setup', data),
  mfaSetup: () => api.post('/auth/mfa/setup'),
  mfaVerifySetup: (code) => api.post('/auth/mfa/verify-setup', { code }),
  mfaVerifyLogin: (mfaToken, code, recoveryCode) => api.post('/auth/mfa/verify-login', { mfaToken, code, recoveryCode }),
  mfaRegenerateRecoveryCodes: () => api.post('/auth/mfa/regenerate-recovery-codes'),
  mfaDisable: (code, recoveryCode) => api.post('/auth/mfa/disable', { code, recoveryCode }),
  getProfile: (config) => api.get('/auth/me', config),
  updateProfile: (data) => api.put('/auth/profile', data),
  updateOnboarding: (action, metadata = {}) => api.patch('/auth/onboarding', { action, ...metadata }),
  createClub: (data, config) => api.post('/auth/onboarding/club', data, config),
  searchClubs: (q) => api.get('/auth/clubs/discovery', { params: { q } }),
  getClubMembershipRequests: () => api.get('/auth/membership-requests'),
  requestClubMembership: (clubId, data, idempotencyKey) => api.post(
    `/auth/clubs/${clubId}/membership-requests`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  ),
  cancelClubMembershipRequest: (requestId) => api.delete(`/auth/membership-requests/${requestId}`),
  confirmClubAuthority: (clubId, policyVersion) => api.post('/auth/legal/club-authority', { clubId, policyVersion }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  getProviders: () => api.get('/auth/providers')
};

// Player endpoints
export const playersAPI = {
  getAll: (teamId, options = {}) => api.get('/players', { params: { ...(teamId ? { teamId } : {}), ...(options.includeStats ? { includeStats: 'true' } : {}) } }),
  getById: (id) => api.get(`/players/${id}`),
  create: (data, config) => api.post('/players', data, config),
  update: (id, data) => api.put(`/players/${id}`, data),
  delete: (id) => api.delete(`/players/${id}`),
  addStats: (id, stats) => api.post(`/players/${id}/stats`, stats),
  updateStats: (id, statId, data) => api.put(`/players/${id}/stats/${statId}`, data),
  deleteStats: (id, statId) => api.delete(`/players/${id}/stats/${statId}`),
  uploadPhoto: (playerId, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    return api.post(`/players/${playerId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePhoto: (playerId) => api.delete(`/players/${playerId}/photo`),
};

// Team endpoints
export const teamsAPI = {
  getAll: () => api.get('/teams'),
  getById: (id) => api.get(`/teams/${id}`),
  create: (data, config) => api.post('/teams', data, config),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  getStatConfig: (id) => api.get(`/teams/${id}/stat-config`),
  getAggregates: (id) => api.get(`/teams/${id}/aggregates`),
  updateStatConfig: (id, data) => api.put(`/teams/${id}/stat-config`, data),
  updateRadarAxes: (id, radarAxes) => api.put(`/teams/${id}/radar-axes`, { radarAxes }),
  applyTemplate: (id, templateKey) => api.post(`/teams/${id}/apply-template`, { templateKey }),
  getTemplatePreview: (id, key) => api.get(`/teams/${id}/template-preview/${key}`),
  bulkImport: (id, data) => api.post(`/teams/${id}/bulk-import`, data),
  rollbackImport: (id, jobId) => api.post(`/teams/${id}/bulk-import/${jobId}/rollback`),
  getImportHistory: (id) => api.get(`/teams/${id}/import-history`),
  updateTargets: (id, targets) => api.put(`/teams/${id}/targets`, { targets })
};

// Session endpoints
export const sessionsAPI = {
  previewCalendarImport: (teamId, payload) => api.post(`/teams/${teamId}/calendar-import/preview`, payload),
  commitCalendarImport: (teamId, payload, idempotencyKey) => api.post(`/teams/${teamId}/calendar-import`, payload, {
    headers: { 'Idempotency-Key': idempotencyKey }, timeout: 180000
  }),
  getAll: (teamId) => api.get('/sessions', { params: teamId ? { teamId } : {} }),
  getById: (id) => api.get(`/sessions/${id}`),
  create: (data) => api.post('/sessions', data),
  update: (id, data) => api.put(`/sessions/${id}`, data),
  delete: (id) => api.delete(`/sessions/${id}`),
  getCalendar: (teamId, start, end) => api.get('/sessions/calendar', {
    params: { teamId, start, end, timezone: resolveViewerTimezone() }
  }),
  getToday: (teamId) => api.get('/sessions/today', {
    params: { teamId, timezone: resolveViewerTimezone() }
  }),
  // R108: upcoming sessions for SchedulePicker (next N days, team-tz aware)
  getUpcoming: (teamId, days = 14) => api.get('/sessions/upcoming', { params: { teamId, days } }),
  // R108: link an existing drill snapshot to a planned session
  linkDrill: (sessionId, drillId) => api.post(`/sessions/${sessionId}/drills`, { drillId }),
  updateAttendance: (id, attendance) => api.put(`/sessions/${id}/attendance`, { attendance }),
  completeSession: (id, data) => api.put(`/sessions/${id}/complete`, data),
  editFeedback: (id, data) => api.put(`/sessions/${id}/feedback`, data),
  // R213 (2026-05-01): per-player attendance summary for Squad Roster Team Stats.
  getTeamAttendanceSummary: (teamId, window = 'season') =>
    api.get(`/sessions/team/${teamId}/attendance-summary`, { params: { window } })
};

export const eventSeriesAPI = {
  capabilities: () => api.get('/event-series/capabilities'),
  preview: (data, config) => api.post('/event-series/preview', data, config),
  create: (data) => api.post('/event-series', data, { timeout: 180000 }),
  getEvent: (eventType, id, timezone) => api.get(`/event-series/event/${eventType}/${id}`, { params: { timezone } }),
  change: (id, data) => api.patch(`/event-series/${id}`, data, { timeout: 180000 }),
  ensureRange: (teamId, endDate) => api.post('/event-series/ensure-range', { teamId, endDate }, { timeout: 180000 }),
};

// AI endpoints
export const aiAPI = {
  generateTrainingPlan: (data) => api.post('/ai/training-plan', data),
  analyzeTeam: (context) => api.post('/ai/team-analysis', { context }),
  chat: (message, history, conversationId, teamName, sport, teamId, sourceType, pinnedDocumentIds) => api.post('/ai/chat', { message, history, conversationId, teamName, sport, teamId, sourceType, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, ...(pinnedDocumentIds && pinnedDocumentIds.length > 0 ? { pinnedDocumentIds } : {}) }),
  getConversations: (teamId) => api.get('/ai/conversations', { params: teamId ? { teamId } : {} }),
  getConversation: (id, teamId) => api.get(`/ai/conversations/${id}`, { params: teamId ? { teamId } : {} }),
  deleteConversation: (id) => api.delete(`/ai/conversations/${id}`),
  newConversation: (teamId) => api.post('/ai/conversations', teamId ? { teamId } : {}),
  getTrainingPlans: (teamId) => api.get('/ai/training-plans', { params: teamId ? { teamId } : {} }),
  getTrainingPlan: (id) => api.get(`/ai/training-plans/${id}`),
  updateTrainingPlan: (id, payload) => api.put(`/ai/training-plans/${id}`, payload),
  deleteTrainingPlan: (id) => api.delete(`/ai/training-plans/${id}`),
  getPlayerAnalyses: (teamId) => api.get('/ai/player-analyses', { params: teamId ? { teamId } : {} }),
  analyzePlayer: (playerId, context, documentContent, teamId, reportType) => api.post('/ai/analyze-player', { playerId, context, documentContent, teamId, reportType }),
  saveAnalysis: (data) => api.post('/ai/player-analyses/save', data),
  deleteAnalysis: (id) => api.delete(`/ai/player-analyses/${id}`),
  getMatchBrief: (teamId) => api.get('/ai/match-brief', { params: { teamId } }),
  getMatchAnalysis: (teamId, matchId) => api.post('/ai/match-analysis', { teamId, matchId }),
  generateDrillLayout: (data) => api.post('/ai/drill-layout', data),
  regenerateDrill: (data) => api.post('/ai/regenerate-drill', data),
  toggleAutoRefetch: (docId, data) => api.patch(`/ai/documents/${docId}/auto-refetch`, data),
  generateDebrief: (data) => api.post('/ai/debrief', data),
  saveDebrief: (data) => api.post('/ai/debrief/save', data),
  // R93: tool-calling proposal flow
  executeProposal: (proposalId, conversationId) =>
    api.post('/ai/chat/execute-proposal', { proposalId, conversationId }, { headers: { 'X-AI-Proposal-Protocol': '2' } }),
  rejectProposal: (proposalId, conversationId, reason) =>
    api.post('/ai/chat/reject-proposal', { proposalId, conversationId, reason }),
  getPendingProposals: (conversationId) =>
    api.get('/ai/chat/proposals', { params: { conversationId } }),
  getProposalResult: (proposalId, conversationId, options = {}) =>
    api.get(`/ai/chat/proposals/${proposalId}/result`, { ...options, params: { conversationId } }),
};

export const coachStartAPI = {
  state: (options = {}) => api.get('/coach-start/state', options),
  start: (options = {}) => api.post('/coach-start/start', {}, options),
  saveDraft: (data, options = {}) => api.put('/coach-start/draft', data, options),
  deleteDraft: (expectedRevision, options = {}) => api.delete('/coach-start/draft', { ...options, data: { expectedRevision } }),
  respond: (data, options = {}) => api.post('/coach-start/respond', data, { timeout: 30000, ...options }),
  displayed: (responseId, options = {}) => api.post('/coach-start/displayed', { responseId }, options),
  feedback: (responseId, feedback, options = {}) => api.post('/coach-start/feedback', { responseId, feedback }, options),
  exposure: (options = {}) => api.post('/coach-start/exposure', {}, options),
  skip: (reason, options = {}) => api.post('/coach-start/skip', { reason }, options),
  previewHandoff: (data, options = {}) => api.post('/coach-start/handoff/preview', data, options),
  confirmHandoff: (receiptId, options = {}) => api.post('/coach-start/handoff/confirm', { receiptId }, options),
  cancelHandoff: (receiptId, options = {}) => api.post('/coach-start/handoff/cancel', { receiptId }, options),
  handoffStatus: (receiptId, options = {}) => api.get(`/coach-start/handoff/${receiptId}`, options),
  insight: (teamId, options = {}) => api.get(`/coach-start/teams/${teamId}/insight`, options),
  insightFeedback: (teamId, data, options = {}) => api.post(`/coach-start/teams/${teamId}/insight/feedback`, data, options),
};

// R223: Per-player practice scrimmage stats.
// opts: { since?: ISO string, until?: ISO string, lastN?: number }
export const scrimmageStatsAPI = {
  getPlayerStats: (teamId, playerId, opts = {}) => {
    const params = {};
    if (opts.since)  params.since  = opts.since;
    if (opts.until)  params.until  = opts.until;
    if (opts.lastN != null) params.lastN = opts.lastN;
    return api.get(`/teams/${teamId}/players/${playerId}/scrimmage-stats`, { params });
  },
  getRounds: (sessionId) => api.get(`/sessions/${sessionId}/scrimmage-rounds`)
};
export const playerFolderAPI = {
  getFolder: (playerId) => api.get(`/player-folder/${playerId}`),
  awardCoachHighFive: (playerId, note = '') =>
    api.post(`/player-folder/${playerId}/achievements/coach-high-five`, { note }),
  getAnalyses: (playerId, type) => api.get(`/player-folder/${playerId}/analyses`, { params: type ? { type } : {} }),
  updateAnalysis: (analysisId, data) => api.put(`/player-folder/analyses/${analysisId}`, data),
  updateAnalysisShare: (analysisId, shareWithParent) => api.patch(`/player-folder/analyses/${analysisId}/share`, { shareWithParent }),
  getPrograms: (playerId) => api.get(`/player-folder/${playerId}/programs`),
  generateProgram: (playerId, data) => api.post(`/player-folder/${playerId}/programs/generate`, data),
  updateProgram: (programId, data) => api.put(`/player-folder/programs/${programId}`, data),
  updateProgramShare: (programId, shareWithParent) => api.patch(`/player-folder/programs/${programId}/share`, { shareWithParent }),
  updateProgramProgress: (programId, data) => api.patch(`/player-folder/programs/${programId}/progress`, data),
  archiveProgram: (programId) => api.post(`/player-folder/programs/${programId}/archive`),
  deleteProgram: (programId) => api.delete(`/player-folder/programs/${programId}`),
  getFilmMentions: (playerId) => api.get(`/player-folder/${playerId}/film-mentions`),
  addCoachNotes: (playerId, data) => api.post(`/player-folder/${playerId}/coach-notes`, data),
};

// Drill Library endpoints
export const drillsAPI = {
  getAll: (teamId) => api.get('/drills', { params: teamId ? { teamId } : {} }),
  // R108: flexible list query for gallery (mine=true, hasDiagram=true, fields=thumb)
  list: (params = {}) => api.get('/drills', { params }),
  getById: (id) => api.get(`/drills/${id}`),
  create: (data) => api.post('/drills', data),
  clone: (id, data = {}) => api.post(`/drills/${id}/clone`, data),
  update: (id, data) => api.put(`/drills/${id}`, data),
  delete: (id) => api.delete(`/drills/${id}`)
};

// Training Blocks endpoints
export const trainingBlocksAPI = {
  getAll: (teamId) => api.get('/training-blocks', { params: { teamId } }),
  getCapabilities: (teamId) => api.get('/training-blocks/capabilities', { params: { teamId } }),
  getById: (id) => api.get(`/training-blocks/${id}`),
  getActive: (teamId, date) => api.get('/training-blocks/active', { params: { teamId, date } }),
  create: (data) => api.post('/training-blocks', data),
  generate: (data) => api.post('/training-blocks/generate', data),
  revise: (id, data) => api.post(`/training-blocks/${id}/revise`, data),
  update: (id, data) => api.put(`/training-blocks/${id}`, data),
  delete: (id) => api.delete(`/training-blocks/${id}`)
};

// Parent Portal endpoints
export const parentAPI = {
  getChildren: () => api.get('/parent/children'),
  getChildOverview: (playerId) => api.get(`/parent/children/${playerId}/overview`),
  getChildReports: (playerId) => api.get(`/parent/children/${playerId}/reports`),
  uploadChildPhoto: (playerId, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    return api.post(`/parent/children/${playerId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteChildPhoto: (playerId) => api.delete(`/parent/children/${playerId}/photo`),
  inviteParent: (playerId, email) => api.post(`/players/${playerId}/parent-invite`, { email }),
  getInviteInfo: (token) => api.get(`/parent-invites/${token}`),
  getInviteRecipientStatus: (token) => api.get(`/parent-invites/${token}/recipient-status`),
  acceptInvite: (token) => api.post(`/parent-invites/${token}/accept`),
  listParentInvites: () => api.get('/parent-invites'),
  revokeInvite: (id) => api.put(`/parent-invites/${id}/revoke`),
  resendInvite: (id) => api.put(`/parent-invites/${id}/resend`),
  // Messages
  getMessages: (playerId, page) => api.get(`/parent/children/${playerId}/messages`, { params: { page } }),
  sendMessage: (playerId, payload) => api.post(
    `/parent/children/${playerId}/messages`,
    typeof payload === 'string' ? { message: payload } : payload
  ),
  uploadDirectMessageImage: (playerId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/parent/children/${playerId}/messages/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  editDirectMessage: (playerId, messageId, message) =>
    api.put(`/parent/children/${playerId}/messages/${messageId}`, { message }),
  deleteDirectMessage: (playerId, messageId) =>
    api.delete(`/parent/children/${playerId}/messages/${messageId}`),
  getUnreadCount: () => api.get('/parent/unread-count'),
  getMessageChannels: () => api.get('/parent/message-channels'),
  getChannelMessages: (channelId) => api.get(`/parent/message-channels/${channelId}/messages`),
  sendChannelMessage: (channelId, payload) => api.post(
    `/parent/message-channels/${channelId}/messages`,
    typeof payload === 'string' ? { message: payload } : payload
  ),
  uploadChannelImage: (channelId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/parent-messages/channels/${channelId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  editChannelMessage: (channelId, messageId, message) =>
    api.put(`/parent-messages/channels/${channelId}/messages/${messageId}`, { message }),
  deleteChannelMessage: (channelId, messageId) =>
    api.delete(`/parent-messages/channels/${channelId}/messages/${messageId}`),
  // RSVP & Schedule
  getSchedule: (playerId, options = {}) => api.get(`/parent/children/${playerId}/schedule`, {
    params: {
      ...(options.includePast ? { includePast: 'true' } : {}),
      ...(options.withinDays ? { withinDays: options.withinDays } : {}),
    },
  }),
  setRsvp: (sessionId, status, playerId, scheduleRevision) => api.put(
    `/parent/sessions/${sessionId}/rsvp`,
    { status, playerId, ...(scheduleRevision === undefined ? {} : { scheduleRevision }) }
  ),
  setMatchRsvp: (matchId, status, playerId, scheduleRevision) => api.put(
    `/parent/matches/${matchId}/rsvp`,
    { status, playerId, ...(scheduleRevision === undefined ? {} : { scheduleRevision }) }
  ),
  // Practice Tips
  getPracticeTips: (playerId) => api.get(`/parent/children/${playerId}/practice-tips`),
  getPracticeSummary: (playerId) => api.get(`/parent/children/${playerId}/practice-summary`),
  updatePracticeTipCompletion: (playerId, tipId, completed, weekKey, variantKey) =>
    api.put(`/parent/children/${playerId}/practice-tips/${tipId}/completion`, {
      completed,
      weekKey,
      variantKey,
    }),
  hasAccess: () => api.get('/parent/has-access'),
};

export const notificationsAPI = {
  getConfig: () => api.get('/notifications/config'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
  subscribe: (subscription) => api.post('/notifications/subscriptions', subscription),
  unsubscribeCurrent: (endpoint) => api.delete('/notifications/subscriptions/current', {
    data: { endpoint },
  }),
};

// Coach-side parent messaging
export const parentMessagesAPI = {
  getConversations: (clubId) => api.get('/parent-messages', { params: clubId ? { clubId } : {} }),
  getUnreadCount: () => api.get('/parent-messages/unread-count'),
  getMessages: (parentUserId, playerId, teamId, page) =>
    api.get(`/parent-messages/${parentUserId}/${playerId}`, { params: { teamId, page } }),
  sendMessage: (parentUserId, playerId, teamId, payload) =>
    api.post(`/parent-messages/${parentUserId}/${playerId}`, {
      teamId,
      ...(typeof payload === 'string' ? { message: payload } : payload),
    }),
  uploadDirectMessageImage: (parentUserId, playerId, teamId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/parent-messages/${parentUserId}/${playerId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { teamId },
    });
  },
  editDirectMessage: (parentUserId, playerId, teamId, messageId, message) =>
    api.put(`/parent-messages/${parentUserId}/${playerId}/messages/${messageId}`, {
      teamId,
      message,
    }),
  deleteDirectMessage: (parentUserId, playerId, teamId, messageId) =>
    api.delete(`/parent-messages/${parentUserId}/${playerId}/messages/${messageId}`, {
      params: { teamId },
    }),
  markRead: (parentUserId, playerId, teamId) =>
    api.put(`/parent-messages/${parentUserId}/${playerId}/read`, { teamId }),
  getAvailableParents: (clubId) =>
    api.get('/parent-messages/available-parents', { params: clubId ? { clubId } : {} }),
  getChannels: (clubId) =>
    api.get('/parent-messages/channels', { params: clubId ? { clubId } : {} }),
  createChannel: (payload) => api.post('/parent-messages/channels', payload),
  updateChannelMembers: (channelId, parentUserIds, membershipVersion) =>
    api.put(`/parent-messages/channels/${channelId}/members`, { parentUserIds, membershipVersion }),
  getChannelMessages: (channelId) => api.get(`/parent-messages/channels/${channelId}/messages`),
  sendChannelMessage: (channelId, payload) => api.post(
    `/parent-messages/channels/${channelId}/messages`,
    typeof payload === 'string' ? { message: payload } : payload
  ),
  uploadChannelImage: (channelId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/parent-messages/channels/${channelId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  editChannelMessage: (channelId, messageId, message) =>
    api.put(`/parent-messages/channels/${channelId}/messages/${messageId}`, { message }),
  deleteChannelMessage: (channelId, messageId) =>
    api.delete(`/parent-messages/channels/${channelId}/messages/${messageId}`),
  deleteChannel: (channelId) => api.delete(`/parent-messages/channels/${channelId}`),
};

export default api;
